import * as React from 'react';

import { API_ROOT } from '../config';
import { useAuthUser } from '../hooks';

let firstFlush = true;

type AnalyticsEvent = Record<string, string | number | null>;
let queue: AnalyticsEvent[] = [];

function getPageUrl(absolute: boolean) {
  return (
    (absolute
      ? document.location.protocol + '//' + document.location.hostname
      : '') +
    document.location.pathname +
    document.location.search
  );
}

function getTimestamp() {
  return Math.floor(new Date().getTime() / 1000);
}

type AnalyticsPayload = {
  events: AnalyticsEvent[];
  p?: string;
  res?: string;
  url?: string;
  refr?: string | null;
  vp?: string;
};

function flush({
  jwt,
}: Readonly<{
  jwt: string | null | undefined;
}>) {
  if (!queue.length) {
    return;
  }

  const referrer = document.referrer || null;
  const deviceRes =
    window.screen.width.toString() + 'x' + window.screen.height.toString();
  const viewport =
    Math.max(
      document.documentElement?.clientWidth || 0,
      window.innerWidth || 0
    ).toString() +
    'x' +
    Math.max(
      document.documentElement?.clientHeight || 0,
      window.innerHeight || 0
    ).toString();

  const payload: AnalyticsPayload = {
    events: queue,
  };
  queue = [];

  if (firstFlush) {
    payload.p = 'web';
    payload.res = deviceRes;
    payload.url = getPageUrl(true);
    payload.refr = referrer;
    payload.vp = viewport;
  }
  firstFlush = false;

  const url = `${API_ROOT}/events/batch`;
  fetch(url, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
}

type AnalyticsEventTuple = [
  type: '_trackPageview' | '_trackEvent',
  category: string | null,
  action: string | null,
  label: string,
  value: number | null,
  nonInteraction: boolean | null,
  property: string
];

type AnalyticsContextType = Readonly<{
  push: (event: AnalyticsEventTuple) => void;
}>;

const AnalyticsContext = React.createContext<AnalyticsContextType | null>(null);

export function useAnalytics(): AnalyticsContextType {
  const context = React.useContext(AnalyticsContext);
  if (!context)
    throw new Error(
      'useAnalytics must be used within AnalyticsContextProvider'
    );
  return context;
}

export function AnalyticsContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const user = useAuthUser();
  const jwt = user?.jwt;

  return (
    <AnalyticsContext.Provider
      value={{
        push: function ([
          type,
          category,
          action,
          label,
          value,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          nonInteraction,
          property,
        ]: AnalyticsEventTuple) {
          if (type === '_trackPageview') {
            queue.push({
              dtm: getTimestamp(),
              se_ca: 'page',
              se_ac: 'view',
              se_la: label || getPageUrl(false),
              se_pr: property || document.title,
            });
          } else if (type === '_trackEvent') {
            queue.push({
              dtm: getTimestamp(),
              se_ca: category,
              se_ac: action,
              se_la: label,
              se_va: value,
              se_pr: property,
            });
          }

          setTimeout(function () {
            flush({ jwt });
          }, 10000);
        },
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
}
